import { Session } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useOutletContext } from "react-router-dom";
import styled from "styled-components";
import { getUserLikedLinks } from "../../../services/getUserLikedLinks";
import { QueryKeys } from "../../../services/types";
import SingleLink from "../../atoms/SingleLink";
import StatusInfo, { StatusInfoVariant } from "../../atoms/StatusInfo";
import Button from "../../atoms/Button";

type LikedLinksSectionProps = {
  tagList:
    | {
        created_at: string;
        id: number;
        name: string | null;
        links_count?: number;
      }[]
    | null
    | undefined;
};

const LikedLinksSection = ({ tagList }: LikedLinksSectionProps) => {
  const [session] = useOutletContext<Session[]>();

  const userLikedLinks = session?.user?.user_metadata?.likedLinks;

  const {
    data: response,
    isLoading,
    refetch,
  } = useQuery({
    queryFn: () => getUserLikedLinks(userLikedLinks),
    queryKey: [QueryKeys.USER_LIKED_LINKS, QueryKeys.ALL_LINKS],
  });

  return (
    <StyledLikedLinksSection>
      <StyledTopSection>
        {(!response?.data?.length || response.data.length === 0) && (
          <StyledNothingHere>
            Nothing here... try refreshing the page if you liked something 😋
          </StyledNothingHere>
        )}

        <Button
          text="Refresh"
          onClick={() => window.location.reload()}
          width="fit-content"
        />
      </StyledTopSection>

      <StyledLikedLinksWrapper>
        {response?.data?.map((link, index) => {
          const matchingTag = tagList?.find((tag) => tag.id === link.tag_id);
          return (
            <SingleLink
              key={`${link.title}_${index}`}
              link={link}
              tagName={matchingTag?.name}
              tagLinksCount={Number(matchingTag?.links_count)}
              pageType="liked"
              refetchPublishedLinks={refetch}
            />
          );
        })}
      </StyledLikedLinksWrapper>

      {isLoading && (
        <StatusInfo variant={StatusInfoVariant.LOADING} text="Loading" />
      )}
      {response?.error?.message && (
        <StatusInfo
          variant={StatusInfoVariant.ERROR}
          text={`${response.status}: ${response.error.message}`}
        />
      )}
    </StyledLikedLinksSection>
  );
};

const StyledLikedLinksSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 30px 20px;
  width: 100%;
  margin-bottom: 10px;
`;

const StyledNothingHere = styled.p`
  text-align: center;
`;

const StyledTopSection = styled.div``;

const StyledLikedLinksWrapper = styled.div`
  display: flex;
  flex-flow: wrap row;
  justify-content: center;
  align-items: flex-start;
  gap: 30px 20px;
`;

export default LikedLinksSection;
